'use client';

import { c as _c } from "react/compiler-runtime";
import { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import type { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import type { SacFilterProps } from '@sola/schema/lib/schema.ts';
import { TrackViewSacItemList } from '@sola/tracking/components/datalayer.tsx';
import { useSacQueryParams } from '../../utils/query-params.sac.tsx';
import { CardList } from '../card-list/card-list.tsx';
import { FeatureButtons } from '../feature-buttons/feature-buttons.tsx';
import { SacCard } from '../sac-card/sac-card.tsx';
import { SacEmpty } from '../sac-empty/sac-empty.tsx';
import type { ResolvedSacFilters } from './sac-list.logic.ts';
import { resolveFiltersFromProps, resolveFiltersFromQueryParams, useSacList } from './sac-list.logic.ts';
import { SacListSkeleton } from './sac-list.skeleton.tsx';
type SacListLayoutProps = {
  /** When true, reads filters from URL params (dynamic), enables show-more and empty state. */
  isDynamic?: boolean;
  columns?: '1' | '2';
  columnsSm?: '1' | '2';
  columnsMd?: '1' | '2' | '3';
  columnsLg?: '1' | '2' | '3';
};
const defaultLimit = 12;
const SacListView: React.FC<{
  resolved: ResolvedSacFilters;
  isDynamic?: boolean;
} & Pick<SacListLayoutProps, 'columns' | 'columnsSm' | 'columnsMd' | 'columnsLg'>> = t0 => {
  const $ = _c(33);
  const {
    resolved,
    isDynamic: t1,
    columns: t2,
    columnsSm: t3,
    columnsMd: t4,
    columnsLg: t5
  } = t0;
  const isDynamic = t1 === undefined ? false : t1;
  const columns = t2 === undefined ? "1" : t2;
  const columnsSm = t3 === undefined ? "1" : t3;
  const columnsMd = t4 === undefined ? "2" : t4;
  const columnsLg = t5 === undefined ? "3" : t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = ["children"];
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const {
    t
  } = useTranslation(t6);
  const {
    isLoading,
    children,
    showRichMix,
    showLoadMoreButton,
    incrementPage,
    resetFilters
  } = useSacList(resolved, isDynamic);
  if (isLoading) {
    let t7;
    if ($[1] !== resolved.limit) {
      t7 = <SacListSkeleton itemCount={resolved.limit} />;
      $[1] = resolved.limit;
      $[2] = t7;
    } else {
      t7 = $[2];
    }
    return t7;
  }
  if (children.length === 0) {
    let t7;
    if ($[3] !== isDynamic || $[4] !== resetFilters) {
      t7 = isDynamic ? <SacEmpty resetFilters={resetFilters} /> : null;
      $[3] = isDynamic;
      $[4] = resetFilters;
      $[5] = t7;
    } else {
      t7 = $[5];
    }
    return t7;
  }
  const t7 = Number(columns);
  const t8 = Number(columnsSm);
  const t9 = Number(columnsMd);
  const t10 = Number(columnsLg);
  let t11;
  if ($[6] !== children) {
    t11 = children.map(_temp);
    $[6] = children;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  let t12;
  if ($[8] !== t10 || $[9] !== t11 || $[10] !== t7 || $[11] !== t8 || $[12] !== t9) {
    t12 = <CardList columns={t7} columnsSm={t8} columnsMd={t9} columnsLg={t10} classNameGap="gap-y-roomy-xs gap-x-comfy-xl">{t11}</CardList>;
    $[8] = t10;
    $[9] = t11;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== children) {
    t13 = <TrackViewSacItemList listId="sac" listName="Sac List" items={children} />;
    $[14] = children;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== isDynamic || $[17] !== showLoadMoreButton || $[18] !== showRichMix || $[19] !== t) {
    t14 = isDynamic && !showLoadMoreButton && <div className="w-full text-center pt-roomy-xs body-md max-w-sm mx-auto">{showRichMix && t("children:sac.useSearchControls")}{!showRichMix && t("children:sac.useClearFilters")}</div>;
    $[16] = isDynamic;
    $[17] = showLoadMoreButton;
    $[18] = showRichMix;
    $[19] = t;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== incrementPage || $[22] !== isDynamic || $[23] !== resetFilters || $[24] !== showLoadMoreButton || $[25] !== showRichMix || $[26] !== t) {
    t15 = isDynamic && <FeatureButtons variant="grow" className="w-full justify-center mt-comfy-xl md:mt-roomy-xs">{showLoadMoreButton && <ButtonStyle><button type="button" onClick={() => incrementPage()}>{t("children:sac.viewMoreChildren")}</button></ButtonStyle>}{!showLoadMoreButton && !showRichMix && <ButtonStyle variant="secondary"><button type="button" onClick={resetFilters}>{t("children:sac.clearFilters")}</button></ButtonStyle>}<ButtonStyle variant={!showLoadMoreButton && !showRichMix ? "primary" : "secondary"}><Link href="/sponsor-a-child/find-your-match/" className="w-full md:w-fit" type="button">{t("children:sac.helpFindMatch")}</Link></ButtonStyle></FeatureButtons>;
    $[21] = incrementPage;
    $[22] = isDynamic;
    $[23] = resetFilters;
    $[24] = showLoadMoreButton;
    $[25] = showRichMix;
    $[26] = t;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t12 || $[29] !== t13 || $[30] !== t14 || $[31] !== t15) {
    t16 = <>{t12}{t13}{t14}{t15}</>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
};
const LiveSacList: React.FC<SacFilterProps & SacListLayoutProps> = ({
  limit = defaultLimit,
  gender,
  countries,
  ageMin,
  ageMax,
  disability,
  hiv,
  orphan,
  highlyVulnerable,
  ...layoutProps
}) => {
  const hasDefaults = gender !== undefined || countries !== undefined || ageMin !== undefined || ageMax !== undefined || disability !== undefined || hiv !== undefined || orphan !== undefined || highlyVulnerable !== undefined;
  const {
    sacFilters
  } = useSacQueryParams({
    isDynamic: true,
    defaults: hasDefaults ? {
      gender: gender as ChildGenderEnum | undefined,
      countries,
      ageMin,
      ageMax,
      disability,
      hiv,
      orphan,
      highlyVulnerable
    } : undefined
  });

  // Depend on individual fields (not the sacFilters object reference) so that
  // unrelated param changes don't recreate `resolved` and force the
  // data-fetching effect in useSacList to re-run.
  const countriesQueryKey = sacFilters.countries?.join(',');
  const filtersQueryKey = sacFilters.filters?.join(',');
  const resolved = useMemo(() => resolveFiltersFromQueryParams(sacFilters, limit), [sacFilters.gender, sacFilters.ageMin, sacFilters.ageMax, countriesQueryKey, sacFilters.birthDay, sacFilters.birthMonth, filtersQueryKey, limit]);
  return <SacListView resolved={resolved} isDynamic {...layoutProps} />;
};
const StaticSacList: React.FC<SacFilterProps & SacListLayoutProps> = ({
  columns,
  columnsSm,
  columnsMd,
  columnsLg,
  limit,
  gender,
  ageMin,
  ageMax,
  countries,
  disability,
  hiv,
  orphan,
  highlyVulnerable
}) => {
  const countriesKey = countries?.join(',');
  const resolved = useMemo(() => resolveFiltersFromProps({
    limit,
    gender,
    ageMin,
    ageMax,
    countries,
    disability,
    hiv,
    orphan,
    highlyVulnerable
  }), [limit, gender, ageMin, ageMax, countriesKey, disability, hiv, orphan, highlyVulnerable]);
  return <SacListView resolved={resolved} columns={columns} columnsSm={columnsSm} columnsMd={columnsMd} columnsLg={columnsLg} />;
};
export const SacList: React.FC<SacFilterProps & SacListLayoutProps> = t0 => {
  const $ = _c(11);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      isDynamic: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const isDynamic = t1 === undefined ? false : t1;
  const limit = props.limit || defaultLimit;
  let t2;
  if ($[3] !== limit) {
    t2 = <SacListSkeleton itemCount={limit} />;
    $[3] = limit;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isDynamic || $[6] !== props) {
    t3 = isDynamic ? <LiveSacList isDynamic={true} {...props} /> : <StaticSacList {...props} />;
    $[5] = isDynamic;
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <Suspense fallback={t2}>{t3}</Suspense>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
function _temp(child) {
  return <SacCard key={child.globalId} {...child} />;
}